import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Mic, ChevronRight, RotateCcw, PlayCircle } from 'lucide-react';
import MathText from './ui/MathText';
import AudioPlayer from './ui/AudioPlayer';
import ClozePassage from './ui/ClozePassage';
import SentenceBuilder from './ui/SentenceBuilder';
import QuizQuestionCard from './QuizQuestionCard';
import QuizSubmissionDetailsModal from './UserQuizSubmissionDetail';
import { useApiCaller } from '../utils/ApiUtilities';
import { getCustomQuizGetByIdUrl, getToeflPerformanceUrl } from '../utils/UrlUtilities';
import { OPTIONFIELDTOCORRECTOPTIONMAP } from '../utils/Constants';
import { loadInProgressAnswers, saveInProgressAnswers, clearInProgressAnswers, loadToeflProgress, saveToeflProgress, clearToeflProgress } from '../utils/BasicUtilities';
import useVoiceDictation from '../utils/useVoiceDictation';
import useAnswerAutosave from '../utils/useAnswerAutosave';
import AnsweredTracker from './ui/AnsweredTracker';
import HighlightableText from './ui/HighlightableText';
import { formatBdPst } from '../utils/DateUtilities';

// ---------------------------------------------------------------------------
// TOEFL model-test taker. Renders a Quiz whose metadata.test_kind === 'toefl_model_test'
// as the real exam: ordered sections → modules → tasks, "no going back" across modules,
// an independent per-module clock, a pre-module instruction screen (with a per-subject
// overview at each section's first module), audio (listening), inline cloze (Complete the
// Words), sentence builder (tiles from the option fields), free-write and speaking
// (dictation transcript, re-answerable, unscored).
//
// Each sub-question is an ordinary 1-point question on the server, so scoring/statistics/
// leaderboards work unchanged. This only assembles + submits answers keyed by question_id:
//   MCQ single -> selected_option "A"      MCQ multi -> "A,C"
//   BUILD_SENTENCE -> selected_option "2,0,1" (ordered tile indices into present options)
//   TEXT (cloze blank / writing / speaking) -> text_answer "..."
// ---------------------------------------------------------------------------

const DEFAULT_MODULE_MINUTES = 15;
// The taker panel sits just below the sticky app navbar (~74px) so its fixed header/timer
// are never overlapped by it.
const NAVBAR_OFFSET = '74px';

const TASK_LABELS = {
    complete_words: 'Complete the Words',
    mcq: 'Multiple choice',
    build_sentence: 'Build a Sentence',
    writing_email: 'Write an Email',
    writing_discussion: 'Academic Discussion',
    speaking_repeat: 'Listen & Repeat',
    speaking_interview: 'Interview',
};

const fmt = (secs) => {
    const s = Math.max(0, secs | 0);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// Per-section sequential question numbers. Numbering restarts at 1 for each section and runs
// continuously across that section's modules → tasks → questions (in blueprint order). Returns
// { question_id: number }. Matches how the real test numbers each skill independently.
function buildNumberByQid(blueprint) {
    const map = {};
    (blueprint?.sections || []).forEach((sec) => {
        let n = 1;
        (sec.modules || []).forEach((m) => {
            (m.tasks || []).forEach((t) => {
                (t.question_ids || []).forEach((id) => { map[id] = n; n += 1; });
            });
        });
    });
    return map;
}


function serializeAnswers(questionsById, answers) {
    const out = [];
    Object.keys(answers).forEach((qid) => {
        const q = questionsById[qid];
        const val = answers[qid];
        const entry = { question_id: Number(qid) };
        const qtype = q?.question_type;
        if (qtype === 'BUILD_SENTENCE') {
            entry.selected_option = Array.isArray(val?.order) ? val.order.join(',') : '';
        } else if (qtype === 'TEXT') {
            entry.text_answer = (val && val.text) || '';
        } else if (Array.isArray(val)) {
            entry.selected_option = val.map((k) => OPTIONFIELDTOCORRECTOPTIONMAP[k]).filter(Boolean).sort().join(',');
        } else if (typeof val === 'string') {
            entry.selected_option = OPTIONFIELDTOCORRECTOPTIONMAP[val] || '';
        }
        out.push(entry);
    });
    return out;
}

function SpeakingTask({ question, answer, onText, voice }) {
    const qid = question.question_id;
    const transcript = (answer && answer.text) || '';
    const listening = voice.listeningField === qid;
    return (
        <div>
            {question.audio_url && <AudioPlayer src={question.audio_url} />}
            <div className="d-flex gap-2 mb-2">
                <button
                    type="button"
                    onClick={() => voice.toggle(qid, transcript, (t) => onText(qid, t))}
                    disabled={!voice.supported}
                    className={`btn ${listening ? 'btn-danger' : 'btn-outline-primary'}`}
                >
                    <Mic size={16} style={{ marginRight: 6 }} />
                    {listening ? 'Stop recording' : (transcript ? 'Continue recording' : 'Record your response')}
                </button>
                {transcript && !listening && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => onText(qid, '')}>
                        <RotateCcw size={16} style={{ marginRight: 6 }} /> Re-answer
                    </button>
                )}
            </div>
            {!voice.supported && <div className="text-muted small">Speech capture needs Chrome or Edge.</div>}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', minHeight: '3rem', background: '#f9fafb', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {transcript || <span className="text-muted">Your transcript will appear here (not editable, not scored).</span>}
            </div>
        </div>
    );
}

export default function ToeflModelTest({ quizParam, onQuizEnd }) {
    const CallApi = useApiCaller();
    const voice = useVoiceDictation();
    const submissionId = quizParam?.submission_id;
    // Restore any saved progress so a mid-test reload (then Resume) lands the user back in the
    // module they left, with their answers intact.
    const savedPos = loadToeflProgress(submissionId);
    const [questionsById, setQuestionsById] = useState({});
    const [blueprint, setBlueprint] = useState(null);
    const [answers, setAnswers] = useState(() => loadInProgressAnswers(submissionId));
    const [moduleIdx, setModuleIdx] = useState(savedPos?.moduleIdx || 0);
    const [started, setStarted] = useState(!!savedPos?.started); // false => show the module's instruction screen
    const [loading, setLoading] = useState(true);
    const [ended, setEnded] = useState(false);
    const [perf, setPerf] = useState(null);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmQuit, setConfirmQuit] = useState(false);
    const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
    const [highlightsByPassage, setHighlightsByPassage] = useState({});
    // For competitions, results stay locked until the competition window (quiz.end_time) closes.
    const [resultsLocked, setResultsLocked] = useState(false);
    const [windowEnd, setWindowEnd] = useState(null);
    const submittedRef = useRef(false);

    // Flatten the blueprint into an ordered list of modules across all sections.
    const modules = useMemo(() => {
        const out = [];
        (blueprint?.sections || []).forEach((sec) => {
            (sec.modules || []).forEach((m, mi) => {
                out.push({ sectionName: sec.name, sectionKey: sec.key, nav: sec.navigation, sectionModuleCount: (sec.modules || []).length, moduleNo: mi + 1, ...m });
            });
        });
        return out;
    }, [blueprint]);

    // Per-section chronological question numbers, keyed by question id (see buildNumberByQid).
    const numberByQid = useMemo(() => buildNumberByQid(blueprint), [blueprint]);

    useEffect(() => {
        (async () => {
            try {
                const res = await CallApi({ url: getCustomQuizGetByIdUrl(quizParam.quiz_id), method: 'GET', token: true });
                if (res.success) {
                    const byId = {};
                    Object.values(res.data.questions || {}).forEach((arr) => arr.forEach((q) => { byId[q.question_id] = q; }));
                    setQuestionsById(byId);
                    setBlueprint(res.data.metadata || { sections: [] });
                    // Competition results (submission details/leaderboard) unlock only after the
                    // competition WINDOW closes (quiz.end_time), not when this attempt ends.
                    const isComp = res.data.quiz_type === 'compitition';
                    const et = res.data.end_time;
                    setResultsLocked(isComp && (!et || new Date(et) > new Date()));
                    setWindowEnd(isComp ? et : null);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [CallApi, quizParam.quiz_id]);

    const setText = useCallback((qid, text) => setAnswers((a) => ({ ...a, [qid]: { text } })), []);
    // Persist answers + position on every change so a reload can resume in the same module.
    useEffect(() => { saveInProgressAnswers(submissionId, answers); }, [submissionId, answers]);
    useEffect(() => { saveToeflProgress(submissionId, { moduleIdx, started }); }, [submissionId, moduleIdx, started]);
    // Server backstop: debounce-save answers so an abandoned attempt (browser closed before the
    // timer fires) can still be finalized+scored by the server's overdue sweep.
    const { scheduleSave, flush } = useAnswerAutosave(submissionId);
    useEffect(() => {
        if (submittedRef.current || Object.keys(answers).length === 0) return;
        scheduleSave(serializeAnswers(questionsById, answers));
    }, [answers, questionsById, scheduleSave]);
    const setOrder = useCallback((qid, order) => setAnswers((a) => ({ ...a, [qid]: { order } })), []);
    const setOption = useCallback((qid, key) => setAnswers((a) => ({ ...a, [qid]: key })), []);
    const toggleMulti = useCallback((qid, key) => setAnswers((a) => {
        const cur = Array.isArray(a[qid]) ? a[qid] : [];
        return { ...a, [qid]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    }), []);

    const submit = useCallback(() => {
        if (submittedRef.current) return;
        submittedRef.current = true;
        setEnded(true);
        voice.stop();
        clearInProgressAnswers(submissionId);
        clearToeflProgress(submissionId);
        Promise.resolve(onQuizEnd({ ...quizParam, submitted_answers: serializeAnswers(questionsById, answers) }))
            .then(async () => {
                try {
                    const res = await CallApi({ url: getToeflPerformanceUrl(quizParam.submission_id), method: 'GET', token: true });
                    if (res.success) setPerf(res.data);
                } catch (e) { /* performance is best-effort */ }
            });
    }, [answers, questionsById, onQuizEnd, quizParam, voice, CallApi]);

    const current = modules[moduleIdx];
    const isLast = moduleIdx >= modules.length - 1;

    const goNext = useCallback(() => {
        voice.stop();
        flush(); // push the latest answers to the server before leaving this module
        if (isLast) submit();
        else { setModuleIdx((i) => i + 1); setStarted(false); }
    }, [isLast, submit, voice, flush]);

    // The countdown effect below intentionally does NOT depend on goNext (so a keystroke doesn't
    // reset the timer). Route expiry through a ref that always points at the latest goNext, so it
    // submits the answers the user actually entered instead of a stale (often empty) snapshot.
    const goNextRef = useRef(goNext);
    useEffect(() => { goNextRef.current = goNext; });

    // Per-module countdown — only while the module is active; auto-advances on expiry.
    // Falls back to a sane default so a blueprint missing duration_minutes never shows 00:00.
    const [secsLeft, setSecsLeft] = useState(0);
    useEffect(() => {
        if (!current || !started) return undefined;
        setSecsLeft((current.duration_minutes || DEFAULT_MODULE_MINUTES) * 60);
        const t = setInterval(() => {
            setSecsLeft((s) => {
                if (s <= 1) { clearInterval(t); goNextRef.current(); return 0; }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
        // `loading` is included so the timer arms once the blueprint finishes loading — on a
        // resumed test `started` is already true while `current` is still undefined mid-fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleIdx, started, loading]);

    const moduleQuestionCount = (m) => (m.tasks || []).reduce((n, t) => n + (t.question_ids || []).length, 0);

    // Cluster a task's MCQs by shared group_id so an audio clip / reading passage renders once.
    const renderGroupedMcq = (qs) => {
        const clusters = [];
        qs.forEach((q) => {
            const last = clusters[clusters.length - 1];
            if (q.group_id && last && last.gid === q.group_id) last.items.push(q);
            else clusters.push({ gid: q.group_id || null, items: [q] });
        });
        return clusters.map((c, ci) => {
            if (!c.gid) {
                return c.items.map((q, i) => (
                    <QuizQuestionCard key={q.question_id} question={q} qIndex={i} displayNumber={numberByQid[q.question_id]} includePassage
                        answer={answers[q.question_id]} onOptionSelect={setOption} onMultiToggle={toggleMulti}
                        onTextChange={setText} onBlankSelect={() => {}} />
                ));
            }
            const head = c.items[0];
            return (
                <div key={`g-${c.gid}-${ci}`} style={{ marginBottom: '1rem', border: '1px solid #e9d5ff', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    {(head.side_text || head.audio_url) && (
                        <div style={{ background: '#f5f3ff', padding: '0.75rem 1rem', borderBottom: '1px solid #e9d5ff' }}>
                            {head.side_text && <HighlightableText className="kp-read-passage" style={{ marginBottom: head.audio_url ? '0.5rem' : 0 }} text={head.side_text} highlights={highlightsByPassage[c.gid] || []} onChange={(next) => setHighlightsByPassage(prev => ({ ...prev, [c.gid]: next }))} />}
                            {head.audio_url && <AudioPlayer src={head.audio_url} />}
                        </div>
                    )}
                    <div style={{ padding: '0.75rem 1rem' }}>
                        {c.items.map((q, i) => (
                            <QuizQuestionCard key={q.question_id} question={q} qIndex={i} displayNumber={numberByQid[q.question_id]} includePassage={false}
                                answer={answers[q.question_id]} onOptionSelect={setOption} onMultiToggle={toggleMulti}
                                onTextChange={setText} onBlankSelect={() => {}} />
                        ))}
                    </div>
                </div>
            );
        });
    };

    const renderTask = (task) => {
        const qs = (task.question_ids || []).map((id) => questionsById[id]).filter(Boolean);
        if (task.task === 'complete_words') return <ClozePassage members={qs} answers={answers} onText={setText} numberByQid={numberByQid} />;
        if (task.task === 'build_sentence') {
            return qs.map((q) => (
                <div key={q.question_id} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: '#fff' }}>
                    <p style={{ color: '#1f2937' }}>{numberByQid[q.question_id] ? <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>Question {numberByQid[q.question_id]}</span> : ''}<MathText text={q.question_text} /></p>
                    <SentenceBuilder question={q} answer={answers[q.question_id]} onOrder={setOrder} />
                </div>
            ));
        }
        const isSpeaking = qs.some((q) => q.metadata && q.metadata.toefl && q.metadata.toefl.capture === 'speaking');
        if (isSpeaking) {
            return qs.map((q) => {
                // Speaking prompts are the audio only — showing the sentence (Listen & Repeat)
                // or the interviewer's question text would defeat the exercise. Hide it for both.
                const isRepeat = q.metadata && q.metadata.toefl && q.metadata.toefl.task === 'speaking_repeat';
                return (
                    <div key={q.question_id} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: '#fff' }}>
                        <p className="text-muted">
                            {numberByQid[q.question_id] ? <strong style={{ color: '#4338ca' }}>{`Q${numberByQid[q.question_id]}. `}</strong> : null}
                            {isRepeat
                                ? 'Listen to the audio, then record yourself repeating it.'
                                : 'Listen to the question in the audio, then record your answer.'}
                        </p>
                        <SpeakingTask question={q} answer={answers[q.question_id]} onText={setText} voice={voice} />
                    </div>
                );
            });
        }
        // Writing free-write (TEXT, no reference) and everything else route through the card;
        // MCQ clusters share their audio/passage once. Writing (email/discussion) gets a tall box.
        const allText = qs.every((q) => q.question_type === 'TEXT');
        if (allText) {
            return qs.map((q, i) => (
                <QuizQuestionCard key={q.question_id} question={q} qIndex={i} displayNumber={numberByQid[q.question_id]} textareaRows={10} includePassage
                    answer={answers[q.question_id]} onOptionSelect={setOption} onMultiToggle={toggleMulti}
                    onTextChange={setText} onBlankSelect={() => {}} />
            ));
        }
        return renderGroupedMcq(qs);
    };

    if (loading) return <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}><div className="modal-dialog modal-dialog-centered"><div className="modal-content p-4 text-center">Loading model test…</div></div></div>;

    if (ended) {
        return (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4">
                        <h4 className="fw-bold mb-2 text-center">Test submitted ✅</h4>
                        {perf ? (() => {
                            // Max scaled total = 30 per SCORED section actually in this quiz
                            // (so a single-section micro = 30, Reading+Listening = 60, not a fixed 120).
                            const scaledMax = (perf.sections || []).filter(s => !s.unscored).length * 30;
                            return (
                            <>
                                <div className="text-center mb-3">
                                    <div className="d-inline-flex gap-2 flex-wrap justify-content-center">
                                        <span className="badge bg-primary fs-6 px-3 py-2">Overall: {perf.total_band != null ? perf.total_band.toFixed(1) : '—'} / 6.0</span>
                                        <span className="badge bg-secondary fs-6 px-3 py-2">{perf.total_scaled} / {scaledMax}</span>
                                    </div>
                                    <div className="text-muted small mt-1">Overall band is the average of the scored sections (rounded to the nearest half-band).</div>
                                </div>
                                <table className="table table-sm align-middle">
                                    <thead><tr><th>Section</th><th className="text-center">Raw</th><th className="text-center">0–30</th><th className="text-center">1–6</th></tr></thead>
                                    <tbody>
                                        {perf.sections.map((s) => (
                                            <tr key={s.name}>
                                                <td>{s.name}</td>
                                                <td className="text-center">{s.unscored ? '—' : `${s.raw}/${s.max}`}</td>
                                                <td className="text-center">{s.unscored ? 'Not scored' : (s.scaled != null ? s.scaled : '—')}</td>
                                                <td className="text-center">{s.unscored ? '—' : (s.band != null ? s.band.toFixed(1) : '—')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot><tr className="fw-bold"><td>Total</td><td className="text-center">{perf.total_raw}/{perf.total_max}</td><td className="text-center">{perf.total_scaled}/{scaledMax}</td><td className="text-center">{perf.total_band != null ? perf.total_band.toFixed(1) : '—'}</td></tr></tfoot>
                                </table>
                                <p className="text-muted small text-center">Both scales are shown: the legacy 0–30 per scored section (total {scaledMax} here) and the 1–6 band. Speaking (and Writing email/discussion) are recorded but not scored.</p>
                            </>
                            );
                        })() : (
                            <p className="text-muted text-center">Reading &amp; Listening are scored; Writing and Speaking are recorded but not scored.</p>
                        )}
                        {resultsLocked ? (
                            <p className="text-muted small text-center mb-2">
                                Your answers are submitted. Full submission details &amp; the leaderboard open
                                after the competition ends{windowEnd ? <> — <strong>{formatBdPst(windowEnd)}</strong></> : null}.
                            </p>
                        ) : (
                            <button className="btn btn-primary w-100 mb-2" onClick={() => setShowSubmissionDetails(true)}>Show Submission Details</button>
                        )}
                        <button className="btn btn-primary w-100" onClick={() => onQuizEnd(null)}>Close</button>
                    </div>
                </div>
                {showSubmissionDetails && (
                    <QuizSubmissionDetailsModal submissionId={submissionId} onClose={() => setShowSubmissionDetails(false)} />
                )}
            </div>
        );
    }
    if (!current) return null;

    // Instruction / Begin screen shown before each module (with a subject overview at the
    // first module of each section). No timer runs until the user clicks Begin.
    if (!started) {
        const firstOfSection = moduleIdx === 0 || modules[moduleIdx - 1].sectionKey !== current.sectionKey;
        const sectionModules = modules.filter((m) => m.sectionKey === current.sectionKey);
        const sectionQs = sectionModules.reduce((n, m) => n + moduleQuestionCount(m), 0);
        const taskTypes = [...new Set((current.tasks || []).map((t) => TASK_LABELS[t.task] || t.task))];
        return (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.6)' }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4">
                        {firstOfSection && (
                            <div className="alert alert-primary">
                                <div className="fw-bold fs-5 mb-1">{current.sectionName} Section</div>
                                <div>{sectionModules.length} module{sectionModules.length > 1 ? 's' : ''} · {sectionQs} question{sectionQs > 1 ? 's' : ''} total.</div>
                                <div className="small mt-1">You cannot return to a module once you move on. Each module is separately timed.</div>
                            </div>
                        )}
                        <h5 className="fw-bold text-dark">{current.sectionName} — {current.name}</h5>
                        <ul className="text-muted">
                            <li>{moduleQuestionCount(current)} question{moduleQuestionCount(current) > 1 ? 's' : ''}</li>
                            <li>Task type{taskTypes.length > 1 ? 's' : ''}: {taskTypes.join(', ')}</li>
                            <li>Time: {current.duration_minutes || DEFAULT_MODULE_MINUTES} minute{(current.duration_minutes || DEFAULT_MODULE_MINUTES) > 1 ? 's' : ''} (the clock starts when you begin)</li>
                        </ul>
                        <button className="btn btn-primary w-100 mt-2" onClick={() => setStarted(true)}>
                            <PlayCircle size={18} style={{ marginRight: 6 }} /> Begin {current.name}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Ordered questions of the current module + whether each is answered → bottom progress boxes.
    const moduleQuestions = (current.tasks || []).flatMap((t) => (t.question_ids || []).map((id) => questionsById[id])).filter(Boolean);
    const isAnswered = (q) => {
        const v = answers[q.question_id];
        if (v == null) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') {
            if (Array.isArray(v.order)) return v.order.length > 0; // BUILD_SENTENCE
            return !!(v.text && v.text.trim());
        }
        return false;
    };

    return (
        <>
        <div style={{ position: 'fixed', top: NAVBAR_OFFSET, left: 0, right: 0, bottom: 0, zIndex: 900, display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
            {/* Fixed header (timer) — sits right below the navbar and stays put while the body scrolls. */}
            <div className="d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(145deg,#6d28d9,#4338ca)', color: '#fff', padding: '0.75rem 1.25rem', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                <div>
                    <div className="fw-bold fs-5">{current.sectionName} — {current.name}</div>
                    <div className="small" style={{ opacity: 0.85 }}>Module {moduleIdx + 1} of {modules.length}</div>
                </div>
                <div className={`d-flex align-items-center gap-2 fs-5 fw-bold${secsLeft <= 60 ? ' kp-timer-flash' : ''}`} style={{ backgroundColor: '#ef4444', color: '#ffffff', padding: '0.3rem 0.8rem', borderRadius: '0.5rem' }} title="Time left in this module">
                    <Clock size={18} /> <span className="small fw-normal">Time left</span> {fmt(secsLeft)}
                </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
                {(current.tasks || []).map((task, ti) => (
                    <div key={ti} style={{ marginBottom: '1.5rem' }}>
                        {task.title && <h5 className="fw-bold text-dark mb-1">{task.title}</h5>}
                        {task.instructions && <p className="text-muted">{task.instructions}</p>}
                        {renderTask(task)}
                    </div>
                ))}
            </div>
            {/* Question progress: one box per question in this module, filled when answered. */}
            {moduleQuestions.length > 0 && (
                <AnsweredTracker questions={moduleQuestions} isAnswered={isAnswered} numberByQid={numberByQid} style={{ flexShrink: 0 }} />
            )}
            <div className="d-flex justify-content-between" style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
                <button className="btn btn-outline-secondary" onClick={() => setConfirmQuit(true)}>Quit &amp; submit</button>
                <button className="btn btn-primary" onClick={() => setConfirmLeave(true)}>
                    {isLast ? 'Finish test' : 'Next module'} <ChevronRight size={16} />
                </button>
            </div>
        </div>
        {confirmLeave && (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4 text-center">
                        <div style={{ fontSize: '2rem' }}>⚠️</div>
                        <h5 className="fw-bold mb-2">This closes {current.name}</h5>
                        <p className="text-muted mb-3">
                            You will <strong>not be able to return</strong> to {current.sectionName} — {current.name} once you continue.
                            Please make sure you have answered everything you want to.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmLeave(false)}>Stay on this module</button>
                            <button className="btn btn-primary" onClick={() => { setConfirmLeave(false); goNext(); }}>
                                {isLast ? 'Finish test' : 'Continue to next module'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        {confirmQuit && (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4 text-center">
                        <div style={{ fontSize: '2rem' }}>🚪</div>
                        <h5 className="fw-bold mb-2">Quit the test?</h5>
                        <p className="text-muted mb-3">
                            Your progress will be <strong>submitted as-is</strong> and the test will end.
                            Unanswered questions stay blank. This can't be undone.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmQuit(false)}>Keep taking the test</button>
                            <button className="btn btn-danger" onClick={() => { setConfirmQuit(false); submit(); }}>Quit &amp; submit</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
